import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type TQueryParamValue =
  string | number | boolean | Array<string | number> | null | undefined;

// Стан фільтрів живе в search-параметрах URL, щоб після перезавантаження
// сторінки чи за посиланням відкривались ті самі дані.
export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParam = (key: string) => searchParams.get(key) ?? undefined;

  const getNumber = (key: string) => {
    const value = searchParams.get(key);
    return value !== null && value !== '' && !Number.isNaN(+value)
      ? +value
      : undefined;
  };

  const getList = (key: string) =>
    searchParams.get(key)?.split(',').filter(Boolean) ?? [];

  // undefined, null, false, '' та порожній масив прибирають параметр з URL.
  const setParams = useCallback(
    (updates: Record<string, TQueryParamValue>) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          Object.entries(updates).forEach(([key, value]) => {
            const isEmpty =
              value === undefined ||
              value === null ||
              value === false ||
              value === '' ||
              (Array.isArray(value) && !value.length);

            if (isEmpty) {
              next.delete(key);
            } else {
              next.set(
                key,
                Array.isArray(value) ? value.join(',') : String(value)
              );
            }
          });

          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return { getParam, getNumber, getList, setParams };
};
